import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
// Must stay after App so main.css keeps the pre-code-splitting rule order.
import './routes/routeStyles';
import { preloadImgCloudRoute } from './routes/definitions';
import reportWebVitals from './reportWebVitals';
import ErrorBoundary from './components/ErrorBoundary';
import { getImgCloudBasename, getImgCloudServiceWorkerRegistration, normalizeLegacyHashRoute } from './utils/appRouting';

const getTodayKey = () => new Date().toISOString().slice(0, 10);

const getPwaClientId = () => {
  const storageKey = 'pwa:client-id';
  try {
    const existing = window.localStorage.getItem(storageKey);
    if (existing) {
      return existing;
    }
    const next = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
    window.localStorage.setItem(storageKey, next);
    return next;
  } catch (error) {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }
};

const detectPlatform = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';
  if (/android/.test(userAgent)) return 'android';
  return 'desktop';
};

const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('micromessenger')) return 'wechat';
  if (userAgent.includes('edg/')) return 'edge';
  if (userAgent.includes('crios') || userAgent.includes('chrome')) return 'chrome';
  if (userAgent.includes('safari')) return 'safari';
  return 'unknown';
};

const isStandaloneDisplay = () => {
  const navigatorWithStandalone = navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigatorWithStandalone.standalone === true
  );
};

const reportPwaEvent = (eventType: string) => {
  const payload = {
    app: 'img-cloud',
    eventType,
    clientId: getPwaClientId(),
    domain: window.location.hostname,
    path: `${window.location.pathname}${window.location.hash || ''}`,
    platform: detectPlatform(),
    browser: detectBrowser(),
    standalone: isStandaloneDisplay(),
  };
  const body = JSON.stringify(payload);
  const blob = new Blob([body], { type: 'application/json' });

  if (navigator.sendBeacon && navigator.sendBeacon('/api/pwa/install-events', blob)) {
    return;
  }

  fetch('/api/pwa/install-events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => undefined);
};

const reportOncePerLocalKey = (key: string, eventType: string) => {
  try {
    if (window.localStorage.getItem(key)) {
      return;
    }
    window.localStorage.setItem(key, '1');
  } catch (error) {
    // Keep telemetry best-effort when storage is unavailable.
  }
  reportPwaEvent(eventType);
};

const registerPwaTelemetry = () => {
  window.addEventListener('beforeinstallprompt', () => {
    reportPwaEvent('install_available');
  });

  window.addEventListener('appinstalled', () => {
    reportOncePerLocalKey('pwa:img-cloud:install-success', 'install_success');
  });

  window.addEventListener('load', () => {
    if (!isStandaloneDisplay()) {
      return;
    }
    reportOncePerLocalKey('pwa:img-cloud:standalone-first-launch', 'standalone_first_launch');
    reportOncePerLocalKey(`pwa:img-cloud:standalone-launch:${getTodayKey()}`, 'standalone_launch');
  });
};

const registerImgCloudServiceWorker = () => {
  const isLocalhost = /^localhost$|^127\.0\.0\.1$/i.test(window.location.hostname);
  if (!('serviceWorker' in navigator) || (!window.isSecureContext && !isLocalhost)) {
    return;
  }

  window.addEventListener('load', () => {
    const registration = getImgCloudServiceWorkerRegistration();

    navigator.serviceWorker
      .register(registration.url, { scope: registration.scope })
      .catch((error) => console.warn('Img-cloud service worker registration failed:', error));
  });
};

const getRouterPathname = (): string => {
  const basename = getImgCloudBasename();
  return basename ? window.location.pathname.slice(basename.length) || '/' : window.location.pathname;
};

const renderApp = () => {
  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  );
  root.render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );

  // 标记React应用已成功加载
  setTimeout(() => {
    (window as any).reactAppLoaded = true;
    console.log('React应用加载完成');
  }, 100); // 短暂延迟确保DOM完全渲染
};

normalizeLegacyHashRoute();

// createRoot replaces the prerendered HTML, so load the page chunk first to avoid a loading flash.
// A failed preload still renders; the route's lazy boundary retries and reports the error.
preloadImgCloudRoute(getRouterPathname()).catch(() => undefined).then(renderApp);

registerImgCloudServiceWorker();
registerPwaTelemetry();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
